import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { AlarmClock, CalendarIcon, Clock } from "lucide-react";
import { toast } from "sonner";
import { el, enGB } from "date-fns/locale";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

const closestQuarter = (mins) => {
  let best = "00", bestDiff = Infinity;
  for (const m of MINUTES) {
    const diff = Math.abs(mins - parseInt(m, 10));
    if (diff < bestDiff) { bestDiff = diff; best = m; }
  }
  return best;
};

export const ReminderScheduleDialog = ({ open, onOpenChange, booking, type, onSaved }) => {
  const { t, lang } = useLang();
  const a = t.admin;
  const r = a.reminder_dialog;
  const [date, setDate] = useState(null);
  const [hour, setHour] = useState("09");
  const [minute, setMinute] = useState("00");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    const current = booking.reminder_scheduled_at ? new Date(booking.reminder_scheduled_at) : null;
    const base = current && !isNaN(current.getTime()) ? current : new Date();
    setDate(base);
    setHour(String(base.getHours()).padStart(2, "0"));
    setMinute(closestQuarter(base.getMinutes()));
  }, [open, booking]);

  const save = async () => {
    if (!date || !booking) return;
    setSaving(true);
    try {
      const when = new Date(date.getFullYear(), date.getMonth(), date.getDate(), parseInt(hour, 10), parseInt(minute, 10));
      await api.patch(`/admin/bookings/${type}/${booking.id}/reminder-schedule`, {
        reminder_scheduled_at: when.toISOString(),
      });
      toast.success(a.reminder_schedule_saved);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || a.save_error);
    } finally {
      setSaving(false);
    }
  };

  const preview = date
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), parseInt(hour, 10), parseInt(minute, 10))
        .toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "full", timeStyle: "short" })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-cream max-w-md rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="reminder-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <AlarmClock className="h-5 w-5" /> {r.title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">
              {booking?.customer_name ? `${booking.customer_name} · ` : ""}{r.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <Label className="text-brand-ink font-semibold flex items-center gap-1.5 mb-2">
              <CalendarIcon className="h-4 w-4 text-brand" /> {r.date_label}
            </Label>
            <div className="rounded-2xl border border-brand/10 bg-white flex justify-center" data-testid="reminder-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={lang === "el" ? el : enGB}
                initialFocus
              />
            </div>
          </div>

          <div>
            <Label className="text-brand-ink font-semibold flex items-center gap-1.5 mb-2">
              <Clock className="h-4 w-4 text-brand" /> {r.time_label}
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger className="rounded-xl border-brand/20 bg-white h-11" data-testid="reminder-hour-select">
                  <SelectValue placeholder={r.hour} />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger className="rounded-xl border-brand/20 bg-white h-11" data-testid="reminder-minute-select">
                  <SelectValue placeholder={r.minute} />
                </SelectTrigger>
                <SelectContent>
                  {MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {preview && (
            <p className="rounded-2xl bg-white border border-brand/10 px-4 py-3 text-sm font-semibold text-brand-ink text-center" data-testid="reminder-preview">
              {preview}
            </p>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-full border-brand/30 text-brand hover:bg-brand-soft/50 font-bold h-11"
              data-testid="reminder-cancel-btn"
            >
              {a.cancel}
            </Button>
            <Button
              onClick={save}
              disabled={!date || saving}
              className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
              data-testid="reminder-save-btn"
            >
              {saving ? a.saving : a.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderScheduleDialog;
